/**
 * PagePerfTracker - Logs page load time on every route change.
 *
 * Measures the time from when the location changes to when React
 * finishes rendering the new page (useEffect fires after paint).
 *
 * Usage: Place once inside <BrowserRouter>, e.g. at the top of App.
 *   <PagePerfTracker />
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { WebLogger } from './WebLogger';
import { WebComponentCode } from './types';

const logger = new WebLogger(WebComponentCode.WEB_PERF, 'PageLoad');

export function PagePerfTracker() {
  const location = useLocation();
  const navStartRef = useRef(performance.now());
  const isFirstRender = useRef(true);

  // Record timestamp synchronously on every location change
  const prevPathRef = useRef(location.pathname);
  if (location.pathname !== prevPathRef.current) {
    navStartRef.current = performance.now();
    prevPathRef.current = location.pathname;
  }

  useEffect(() => {
    const loadTime = Math.round(performance.now() - navStartRef.current);

    const log = loadTime > 1000 ? logger.warn.bind(logger) : logger.info2.bind(logger);

    if (isFirstRender.current) {
      // Defer initial log so LoggerProvider has time to register providers
      isFirstRender.current = false;
      setTimeout(() => {
        log(`${location.pathname} loaded`, { loadTimeMs: loadTime, path: location.pathname, initial: true });
      }, 0);
    } else {
      log(`${location.pathname} loaded`, { loadTimeMs: loadTime, path: location.pathname });
    }
  }, [location.pathname]);

  return null;
}
